<script lang="ts" setup generic="T">
import { computed, useId } from "vue";
import { useDensity } from "../../composables/density.js";
import { useRequired } from "../../composables/required.js";
import { useVModel } from "../../composables/useVModel.js";
import type { Nullable } from "../../types/index.js";
import { FORM_INJECTED_SYMBOL, useFormContext } from "../OnyxForm/OnyxForm.core.js";
import OnyxInfoTooltip from "../OnyxInfoTooltip/OnyxInfoTooltip.vue";
import FormMessage from "./FormMessage.vue";
import type { OnyxFormElementProps } from "./types.js";

const props = withDefaults(defineProps<OnyxFormElementProps<T>>(), {
  required: false,
  disabled: FORM_INJECTED_SYMBOL,
  showError: FORM_INJECTED_SYMBOL,
  requiredMarker: FORM_INJECTED_SYMBOL,
  reserveMessageSpace: FORM_INJECTED_SYMBOL,
  id: () => useId(),
});

const emit = defineEmits<{
  "update:modelValue": [value?: Nullable<T>];
}>();

const { requiredMarker, reserveMessageSpace } = useFormContext(props);
const { requiredMarkerClass, requiredTypeClass } = useRequired(props, requiredMarker);
const { densityClass } = useDensity(props);

/**
 * Current value of the input.
 */
const modelValue = useVModel({
  props,
  emit,
  key: "modelValue",
});

const counter = computed(() => {
  if (props.withCounter && props.maxlength) {
    const length = (modelValue.value?.toString() ?? "").length;
    const maxLength = typeof props.maxlength === "object" ? props.maxlength.max : props.maxlength;
    const violated = length > maxLength;

    return {
      length,
      maxLength,
      violated,
    };
  }
  return undefined;
});

defineSlots<{
  /**
   * The place for the actual form element
   */
  default(props: { id: string }): unknown;
}>();

/**
 * All footer related props. Used as convenience because if all properties are undefined, undefined
 * itself will be returned instead of an empty object. So the footer can easily be hidden when no
 * relevant props are set.
 */
const footer = computed(() => {
  const { errorMessages, successMessages, message } = props;

  const data = {
    counter: counter.value,
    errorMessages,
    successMessages,
    message,
  };

  for (const key in data) {
    const _key = key as keyof typeof data;
    // remove nullish keys from object
    if (data[_key] == undefined) delete data[_key];
  }

  if (Object.keys(data).length === 0) return undefined;
  return data;
});
</script>

<template>
  <div
    :class="[
      'onyx-component',
      'onyx-form-element',
      requiredTypeClass,
      successMessages ? 'onyx-form-element--success' : undefined,
      densityClass,
    ]"
  >
    <div v-if="!props.hideLabel" class="onyx-form-element__label onyx-text--small">
      <label :for="props.id" class="onyx-truncation-ellipsis">{{ props.label }}</label>
      <span
        v-if="props.required"
        :class="[props.required ? requiredMarkerClass : undefined]"
      ></span>
      <OnyxInfoTooltip
        v-if="props.labelTooltip"
        class="onyx-form-element__label-tooltip"
        trigger="hover"
        :text="props.labelTooltip"
      />
      <span
        v-if="!props.required"
        :class="[!props.required ? requiredMarkerClass : undefined]"
      ></span>
    </div>

    <slot :id="props.id"></slot>

    <!-- the "v-if" here is imported so the footer is not renderer when its empty. This is needed so that other elements (e.g. buttons) are vertically aligned correctly -->
    <div
      v-if="reserveMessageSpace || footer"
      :class="{
        'onyx-text--small': true,
        'onyx-form-element__footer': true,
        ['onyx-form-element__footer--reserved']: reserveMessageSpace,
      }"
    >
      <span class="onyx-form-element__footer-messages">
        <FormMessage
          v-if="footer?.errorMessages"
          class="onyx-form-element__error-message"
          :messages="footer.errorMessages"
          type="danger"
        />
        <FormMessage
          v-if="footer?.successMessages"
          class="onyx-form-element__success-message"
          :messages="footer.successMessages"
          type="success"
        />
        <FormMessage
          v-if="footer?.message"
          class="onyx-form-element__message"
          :messages="footer.message"
          type="neutral"
        />
      </span>
      <span
        v-if="footer?.counter"
        :class="{
          'onyx-form-element__counter': true,
          'onyx-form-element__counter--violated': footer.counter.violated,
        }"
      >
        {{ footer.counter.length }}/{{ footer.counter.maxLength }}
      </span>
    </div>
  </div>
</template>

<style lang="scss">
@use "../../styles/mixins/layers.scss";

.onyx-use-optional:not(:has(.onyx-required-marker)) {
  @include layers.component() {
    .onyx-form-element__optional {
      display: inline-block;
    }
  }
}

@include layers.override() {
  .onyx-form-element__footer--reserved {
    height: 1lh;
    display: flex;
  }
}

.onyx-form-element {
  @include layers.component() {
    font-family: var(--onyx-font-family-paragraph);
    display: flex;
    flex-direction: column;
    gap: var(--onyx-density-3xs);

    &__label {
      display: flex;
      align-items: center;
      max-width: 100%;
      width: 100%;
      color: var(--onyx-color-text-icons-neutral-medium);

      // optional marker should be displayed at the very end of the label
      & .onyx-optional-marker {
        flex-grow: 1;
        text-align: end;
      }
    }

    &__optional {
      display: none;
      font-family: var(--onyx-font-family-paragraph);
      font-weight: var(--onyx-font-weight-regular);
      font-style: normal;
      color: var(--onyx-color-text-icons-neutral-soft);
      padding-left: var(--onyx-density-xs);
      hyphens: none;
      margin-left: auto;
    }

    $footer-gap: var(--onyx-spacing-2xs);

    &__label-tooltip {
      margin-left: $footer-gap;
    }

    &__footer {
      width: 100%;
      max-width: 100%;
      display: none;
      justify-content: space-between;

      gap: $footer-gap;
      color: var(--onyx-color-text-icons-neutral-soft);
    }

    // only show footer when it has visible content
    &:has(.onyx-form-message__danger) {
      .onyx-form-element__footer {
        display: var(--error-message-display, none);
      }
    }
    &:has(.onyx-form-message__neutral, .onyx-form-message__success, .onyx-form-element__counter) {
      .onyx-form-element__footer {
        display: flex;
      }
    }

    &__footer-messages {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
    }

    &__counter {
      max-width: fit-content;
    }

    &__counter--violated {
      color: var(--onyx-color-text-icons-danger-intense);
    }

    &__error-message,
    &__error-tooltip {
      display: var(--error-message-display, none);
      color: var(--onyx-color-base-danger-500);
    }

    &__success-message,
    &__success-tooltip {
      display: var(--success-message-display, flex);
      color: var(--onyx-color-base-success-700);
    }

    &__message {
      display: var(--message-display, flex);
    }
  }
}
</style>
